import { useEffect, useRef, useState } from "react";

/**
 * Each draft save overwrites the learner's whole saved answer list, so two
 * requests in flight at once could let the older one land last and erase
 * newer answers. The queue keeps at most one request out and, behind it,
 * only the newest state.
 */
export interface DraftSaveQueue<T> {
  /** Queues `state`, replacing any state still waiting to be sent. Once the
   *  queue is closed it sends nothing and reports the stray save to
   *  `onError`. */
  save: (state: T) => void;
  /** Settles once nothing is queued or in flight, first resending the newest
   *  state if its last attempt failed. Resolves either way: a failure has
   *  already gone to `onError`. */
  drain: () => Promise<void>;
  /** Whether the newest state queued is confirmed saved by a request that
   *  can only have landed after every older one, with no write from outside
   *  the queue still able to overwrite it. True when nothing was ever
   *  queued. */
  isSaved: () => boolean;
  /** Marks the start of a write of the same draft made outside the queue,
   *  which carries the state as it stood now. Returns the call that marks
   *  its end, once it has settled either way. States queued after the start
   *  count as unsaved until then, and are sent again at the end so they land
   *  after it. */
  beginExternalWrite: () => () => void;
  /** Sends the newest unsaved state now, with a request that outlives the
   *  page. Does nothing when everything queued is saved, or when that
   *  state's keepalive request is already out. */
  flushBeforeUnload: () => void;
  /** Drops anything waiting and sends nothing more, once the draft no longer
   *  exists to save to. */
  close: () => void;
}

// Saves still out from every queue, a provider that has since unmounted
// included: leaving the assessment and coming back mounts a new provider,
// whose start and submit must not race the old one's saves.
const outstandingSaves = new Set<Promise<void>>();
const DRAFT_SAVES_WAIT_MS = 10_000;

/** Settles once no draft save from any queue is out, or after a bounded wait
 *  so a hung request cannot hold the caller for good. Null when nothing is
 *  out, so a caller with nothing to wait for goes ahead at once. */
export function whenDraftSavesSettle(): Promise<void> | null {
  if (outstandingSaves.size === 0) return null;
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, DRAFT_SAVES_WAIT_MS);
    const settleAll = async () => {
      while (outstandingSaves.size > 0) {
        await Promise.all(Array.from(outstandingSaves));
      }
    };
    settleAll().then(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

export type SendDraft<T> = (
  state: T,
  options: { keepalive: boolean }
) => Promise<unknown>;

export function createDraftSaveQueue<T>(
  send: SendDraft<T>,
  onError: (error: unknown) => void
): DraftSaveQueue<T> {
  type Entry = { state: T; version: number };
  let versions = 0;
  let latest: Entry | null = null;
  let savedVersion = 0;
  let waiting: Entry | null = null;
  let inFlight: Promise<void> | null = null;
  let keepaliveVersion: number | null = null;
  let closed = false;
  // The newest state as each outstanding outside write began. Compared by
  // identity, not version: sending the same state again keeps the object, so
  // only a state queued since then counts as newer.
  type ExternalWrite = { startState: T | undefined };
  const externalWrites = new Set<ExternalWrite>();

  const isNewerThan = (write: ExternalWrite) =>
    latest !== null && latest.state !== write.startState;

  const hasUnconfirmed = () => latest !== null && latest.version > savedVersion;
  const hasUnsaved = () =>
    hasUnconfirmed() || Array.from(externalWrites).some(isNewerThan);

  // Resolves either way: a failed save is reported, and the next answer
  // carries the full list again, so it retries everything the failed one held.
  const attempt = (entry: Entry, keepalive: boolean) =>
    send(entry.state, { keepalive }).then(() => {
      savedVersion = Math.max(savedVersion, entry.version);
    }, onError);

  const track = (request: Promise<void>) => {
    const tracked: Promise<void> = request.finally(() => {
      if (inFlight !== tracked) return;
      inFlight = null;
      if (waiting && !closed) sendWaiting();
    });
    inFlight = tracked;
    outstandingSaves.add(tracked);
    tracked.then(() => outstandingSaves.delete(tracked));
  };

  const sendWaiting = () => {
    const entry = waiting!;
    waiting = null;
    track(attempt(entry, false));
  };

  const sendKeepalive = (
    entry: Entry,
    request: (entry: Entry) => Promise<void>
  ) => {
    keepaliveVersion = entry.version;
    return request(entry).finally(() => {
      if (keepaliveVersion === entry.version) keepaliveVersion = null;
    });
  };

  return {
    save(state) {
      // Reported rather than thrown: this runs from an effect, where a throw
      // would unmount the assessment just after it was submitted.
      if (closed) {
        onError(new Error("Draft save requested after the queue was closed"));
        return;
      }
      latest = { state, version: ++versions };
      waiting = latest;
      if (!inFlight) sendWaiting();
    },

    // Only what the queue itself has not confirmed: an outside write still
    // out is answered by the resend at its end, not by sending again now.
    async drain() {
      if (!closed && hasUnconfirmed() && !inFlight) {
        waiting = latest;
        sendWaiting();
      }
      while (inFlight) await inFlight;
    },

    isSaved: () => !hasUnsaved(),

    beginExternalWrite() {
      const write: ExternalWrite = { startState: latest?.state };
      externalWrites.add(write);
      return () => {
        if (!externalWrites.delete(write)) return;
        if (closed || latest === null || !isNewerThan(write)) return;
        // Sent again under a fresh version, so a request already out for the
        // same state cannot confirm it: that one may have landed before the
        // outside write did.
        latest = { state: latest.state, version: ++versions };
        waiting = latest;
        if (!inFlight) sendWaiting();
      };
    },

    // While an outside write is out, the keepalive can still lose the race to
    // it on the server, but sent later from the same client it usually lands
    // later too.
    flushBeforeUnload() {
      if (closed || !hasUnsaved() || keepaliveVersion === latest!.version) {
        return;
      }
      const entry = latest!;
      if (!inFlight) {
        waiting = null;
        track(sendKeepalive(entry, (e) => attempt(e, true)));
        return;
      }
      // The page may be gone before the request already out settles, so this
      // one cannot wait for it. If the page survives, the server may still
      // commit the older request last, so this one confirms nothing and the
      // newest state is re-sent in order once both settle.
      const keepalive = sendKeepalive(entry, (e) =>
        send(e.state, { keepalive: true }).then(() => undefined, onError)
      );
      waiting = entry;
      track(Promise.all([inFlight, keepalive]).then(() => undefined));
    },

    close() {
      closed = true;
      waiting = null;
    },
  };
}

/** A draft save queue that lives as long as the component, always sending
 *  through the latest `send` and `onError` it was rendered with, and flushed
 *  with a keepalive request when the tab is hidden or closed while
 *  `flushEnabled` is true in the latest render. */
export function useDraftSaveQueue<T>(
  send: SendDraft<T>,
  onError: (error: unknown) => void,
  flushEnabled: boolean
): DraftSaveQueue<T> {
  const sendRef = useRef(send);
  const onErrorRef = useRef(onError);
  const flushEnabledRef = useRef(flushEnabled);
  sendRef.current = send;
  onErrorRef.current = onError;
  flushEnabledRef.current = flushEnabled;

  const [queue] = useState(() =>
    createDraftSaveQueue<T>(
      (state, options) => sendRef.current(state, options),
      (error) => onErrorRef.current(error)
    )
  );

  useEffect(() => {
    const flush = () => {
      if (flushEnabledRef.current) queue.flushBeforeUnload();
    };
    const onPageHide = () => flush();
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [queue]);

  return queue;
}
